import { useState } from 'react';
import { ViewerModalFrame, ViewerModalSection, type ViewerModalTheme } from './ViewerModalFrame';

export interface FAQ {
  label: string;
  href: string;
}

interface HelpSupportModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  askPlaceholder?: string;
  askButtonLabel?: string;
  commonQuestionsTitle?: string;
  commonQuestions?: FAQ[];
  contactTitle?: string;
  contactEmail?: string;
  contactPhone?: string;
  onAsk?: (question: string) => void;
  theme?: ViewerModalTheme;
}

export function HelpSupportModal({
  open,
  onClose,
  title = 'Help & Support',
  askPlaceholder = 'Type your question...',
  askButtonLabel = 'Send',
  commonQuestionsTitle = 'Common questions',
  commonQuestions = [],
  contactTitle = 'Contact us',
  contactEmail = 'support@metaphysical-labs.com',
  contactPhone = '',
  onAsk,
  theme,
}: HelpSupportModalProps) {
  const [text, setText] = useState('');

  const t = {
    primaryTextColor: 'rgba(255, 255, 255, 0.95)',
    secondaryTextColor: 'rgba(255, 255, 255, 0.7)',
    accentColor: 'rgb(56, 189, 248)',
    borderColor: 'rgba(255, 255, 255, 0.1)',
    ...theme,
  };

  const handleSend = () => {
    if (onAsk && text.trim()) {
      onAsk(text.trim());
    }
    setText('');
  };

  return (
    <ViewerModalFrame open={open} onClose={onClose} title={title} titleIcon="support_agent" theme={theme}>
      {/* Ask a question */}
      <ViewerModalSection theme={theme}>
        <p style={{ color: t.secondaryTextColor, fontSize: '0.8125rem', margin: '0 0 0.75rem' }}>
          How can we help?
        </p>
        <div style={{ display: 'flex' }}>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
            placeholder={askPlaceholder}
            style={{
              flex: 1,
              padding: '0.65rem 0.85rem',
              border: `1px solid ${t.borderColor}`,
              borderRight: 'none',
              borderRadius: '0.75rem 0 0 0.75rem',
              background: 'rgba(255, 255, 255, 0.04)',
              color: t.primaryTextColor,
              fontSize: '0.8rem',
              outline: 'none',
              fontFamily: 'inherit',
            }}
          />
          <button
            onClick={handleSend}
            style={{
              background: t.accentColor,
              color: '#0f1219',
              padding: '0.65rem 0.85rem',
              border: 'none',
              borderRadius: '0 0.75rem 0.75rem 0',
              fontSize: '0.8rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {askButtonLabel}
          </button>
        </div>
      </ViewerModalSection>

      {/* FAQ links */}
      {commonQuestions.length > 0 && (
        <ViewerModalSection title={commonQuestionsTitle} theme={theme}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {commonQuestions.map((q, idx) => (
              <a
                key={idx}
                href={q.href}
                target="_blank"
                rel="noreferrer"
                style={{
                  color: t.accentColor,
                  fontSize: '0.8125rem',
                  textDecoration: 'none',
                  transition: 'opacity 0.15s',
                }}
              >
                {q.label}
              </a>
            ))}
          </div>
        </ViewerModalSection>
      )}

      {/* Contact info */}
      <ViewerModalSection title={contactTitle} theme={theme}>
        {contactEmail && (
          <p style={{ fontSize: '0.8125rem', color: t.secondaryTextColor, margin: '0 0 0.25rem' }}>
            {contactEmail}
          </p>
        )}
        {contactPhone && (
          <p style={{ fontSize: '0.8125rem', color: t.secondaryTextColor, margin: 0 }}>
            {contactPhone}
          </p>
        )}
        {!contactEmail && !contactPhone && (
          <p style={{ fontSize: '0.8125rem', color: t.secondaryTextColor, margin: 0 }}>
            No contact information configured.
          </p>
        )}
      </ViewerModalSection>
    </ViewerModalFrame>
  );
}
